import { LevelComponent, LevelType } from '../Level/ILevelState';
import { UnlockType } from '../Player/IPlayerState';
import LevelStateHelper from '../Level/LevelStateHelper';
import { LevelTask } from '../Level/LevelTask';
import {
    setBoosterBullsEye,
    setBoosterShuffle,
    setBoosterTNT,
} from '../Player/PlayerInventoryHelper';
import { IGameState } from './IGameState';

export default class FTUXStateHelper {
    /**
     * Set the enabled components for the current level based on player
     * unlocks (suitcase, boosters, etc.) and level number.
     * @param state
     */
    public static setLevelComponents(state: IGameState): IGameState {
        const components: LevelComponent[] = [];

        if (
            state.levelState.levelNumber > 1 ||
            state.levelState.levelType === LevelType.CHALLENGE
        ) {
            /* prettier-ignore */
            if (state.playerState.playerUnlocks.find(unlock => unlock.type === UnlockType.SUITCASE)) {
                components.push(LevelComponent.SUITCASE);
            }
            /* prettier-ignore */
            if (state.playerState.playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_TNT)) {
                components.push(LevelComponent.BOOSTER_TNT);
            }
            /* prettier-ignore */
            if (state.playerState.playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_BULLS_EYE)) {
                components.push(LevelComponent.BOOSTER_BULLS_EYE);
            }
            /* prettier-ignore */
            if (state.playerState.playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_SHUFFLE)) {
                components.push(LevelComponent.BOOSTER_SHUFFLE);
            }
        }

        if (
            state.levelState.levelNumber >= 2 ||
            state.levelState.levelType === LevelType.CHALLENGE
        ) {
            components.push(LevelComponent.SETTINGS);
            components.push(LevelComponent.COINS_DISPLAY);
            components.push(LevelComponent.BOOSTER_PLUS_FIVE);
        }

        let streakEnabled = true;
        if (
            state.levelState.levelNumber >= 3 ||
            state.levelState.levelType === LevelType.CHALLENGE
        ) {
            components.push(LevelComponent.BONUS_STREAK);
        } else {
            streakEnabled = false;
        }

        if (
            state.levelState.levelNumber >= 4 ||
            state.levelState.levelType === LevelType.CHALLENGE
        ) {
            components.push(LevelComponent.BOOSTER_UNDO);
        }

        if (
            state.levelState.levelNumber >= 6 ||
            state.levelState.levelType === LevelType.CHALLENGE
        ) {
            components.push(LevelComponent.BOOSTER_JOKER);
        }

        return {
            ...state,
            levelState: {
                ...state.levelState,
                components: components,
                cardsState: {
                    ...state.levelState.cardsState,
                    streakEnabled: streakEnabled,
                },
            },
        };
    }

    // tslint:disable-next-line: cognitive-complexity
    public static setLevelTasks(state: IGameState): IGameState {
        if (state.levelState.levelType === LevelType.CHALLENGE) return state;

        let playerState = state.playerState;
        // Check for and set player unlocks at particular levels.
        const playerUnlocks = state.playerState.playerUnlocks.slice(0);

        if (state.levelState.levelNumber === 1) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_CARD_MATCH,
            });
        }

        if (state.levelState.levelNumber === 2) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_CARD_DECK,
            });
        }

        if (state.levelState.levelNumber === 3) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_BONUS_STREAK,
            });
        }

        if (
            state.levelState.levelNumber === 4 &&
            // If suitcase is enabled, ignore the FTUX text.
            !playerUnlocks.find(unlock => unlock.type === UnlockType.SUITCASE)
        ) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_BOOSTER_UNDO,
            });
        }

        if (state.levelState.levelNumber === 6) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_BOOSTER_JOKER,
            });
        }

        if (state.levelState.levelNumber === 10) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_HAZARD_BOMB,
            });
        }

        if (state.levelState.levelNumber === 26) {
            /* prettier-ignore */
            if (!playerUnlocks.find(unlock => unlock.type === UnlockType.SUITCASE)) {
                playerUnlocks.push({ type: UnlockType.SUITCASE });
            }
            /* prettier-ignore */
            if (!playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_TNT)) {
                playerUnlocks.push({ type: UnlockType.BOOSTER_TNT });

                // Give 3x free TNT boosters.
                playerState = setBoosterTNT(
                    state.playerState,
                    state.playerState.inventory.boosterTNT + 3,
                    false
                );

                state = LevelStateHelper.setStateTaskData(state, {
                    task: LevelTask.FTUX_BOOSTER_TNT,
                });
            }
        }

        if (
            state.levelState.levelNumber === 40 &&
            /* prettier-ignore */
            !playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_BULLS_EYE)
        ) {
            playerUnlocks.push({ type: UnlockType.BOOSTER_BULLS_EYE });

            // Give 1x free bulls-eye boosters.
            playerState = setBoosterBullsEye(
                state.playerState,
                state.playerState.inventory.boosterBullsEye + 1,
                false
            );

            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_BOOSTER_BULLS_EYE,
            });
        }

        if (
            state.levelState.levelNumber === 45 &&
            /* prettier-ignore */
            !playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_SHUFFLE)
        ) {
            playerUnlocks.push({ type: UnlockType.BOOSTER_SHUFFLE });

            // Give 1x free shuffle boosters.
            playerState = setBoosterShuffle(
                state.playerState,
                state.playerState.inventory.boosterShuffle + 1,
                false
            );

            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_BOOSTER_SHUFFLE,
            });
        }

        if (state.levelState.levelNumber === 858) {
            /* prettier-ignore */ state = LevelStateHelper.setStateTaskData(state, { task: LevelTask.FTUX_GOLD_CARDS });
        }

        return {
            ...state,
            levelState: {
                ...state.levelState,
            },
            playerState: {
                ...playerState,
                playerUnlocks: playerUnlocks,
            },
        };
    }
}
